import React, { useState, useEffect, useContext, useMemo } from 'react';
import Categories from '../components/Categories/Categories';
import Question from '../components/Question/Question';
import Loader from '../../../widgets/plug/Loader/Loader';
import { svgSprite } from '../../../hybrid/wrappers';
import { useWindowDimensions } from '../../../common/utils/windowDimensions';
import T, { t } from '../../../common/components/T';
import { LangContext } from '../../../common/decorators/Language';
import Breadcrumbs from '../../../widgets/breadcrumbs/AdvancedSingleBreadCrumbs/AdvancedSingleBreadCrumbs';
import css from './FAQ.module.scss';
import LazyLoad from 'react-lazy-load';
import Plug from '../../../widgets/plug/Plug/Plug';

export const FAQ = ({ data, loadMore }) => {
    const { translation } = useContext(LangContext) || {};
    const widthDimensions = useWindowDimensions();
    const isMobile = useMemo(() => (!widthDimensions ? null : widthDimensions < 576), [widthDimensions]);

    const categoriesData = useMemo(() => {
        if (!isMobile) {
            return data?.categories?.length
                ? data?.categories?.map((item) => ({
                      ...item,
                      isActive: data?.categories?.[0].id === item.id,
                      anchorId: `category-${item.id}`
                  }))
                : [];
        } else {
            return data?.categories?.length
                ? data?.categories?.map((item) => ({
                      ...item,
                      anchorId: `category-${item.id}`
                  }))
                : [];
        }
    }, [data?.categories, isMobile]);

    const [categories, setCategories] = useState(categoriesData);

    useEffect(() => {
        setCategories(categoriesData);
    }, [categoriesData]);

    const [question, setQuestion] = useState(data?.question);
    const [isLoading, setIsLoading] = useState(false);
    const [activeQuestionId, setActiveQuestionId] = useState(null);

    useEffect(() => {
        if (isMobile) {
            setQuestion({ questions: null });
        }
    }, [isMobile]);

    const activatedQuestion = (id) => {
        setActiveQuestionId((prev) => (prev === id ? null : id));
    };

    const handleBack = () => {
        setQuestion({ questions: null });
        setCategories(categoriesData);
    };

    const handleClickCategories = (id) => {
        if (isMobile) {
            setIsLoading(true);
        }
        (async () => {
            if (!isMobile) {
                setCategories((prev) =>
                    prev.map((item) => ({
                        ...item,
                        isActive: id === item.id
                    }))
                );
            } else {
                if (!categories) {
                    return;
                }
                const [activeCategory] = categories.filter((item) => item.id === id);
                setCategories({ activeCategory });
            }
            const questionResponse = categories?.find((el) => el?.id === id);
            if (questionResponse) {
                const newArrQuestion = questionResponse.questions
                    ?.map((item) => ({
                        ...item,
                        isActive: false
                    }))
                    ?.filter((item) => item.status === 'active');
                setQuestion({ ...questionResponse, questions: newArrQuestion });
            }
            if (isMobile) {
                setIsLoading(false);
            }
        })();
    };

    if (!categories) return <Loader />;
    if (categories.length === 0)
        return (
            <div className={css.faqVeiwsWrap}>
                <div className={css.faqNavigationWrap}>
                    <Breadcrumbs
                        data={{
                            title: t('mainPageTitle-faq', 'FAQ', '/faq', translation),
                            link: '/faq'
                        }}
                        handleBack={categories?.activeCategory ? handleBack : false}
                    />
                </div>
                <div className={css.emptyNews}>
                    <Plug
                        plugImg={'/src/img/plugs/no_news.svg'}
                        plugTitle={<T textName='faqPage-faqQuestion' defaultStr='Пока нет вопросов ответов' page='/faq' />}
                    />
                </div>
            </div>
        );

    return (
        <div className={css.faqVeiwsWrap}>
            <div className={css.faqNavigationWrap}>
                <Breadcrumbs
                    data={{
                        title: t('mainPageTitle-faq', 'FAQ', '/faq', translation),
                        link: '/faq'
                    }}
                    handleBack={categories?.activeCategory ? handleBack : false}
                />
            </div>

            <div className={css.faqContentWrapper}>
                <div className={css.faqVeiwsWrapQuestions}>
                    {!isMobile &&
                        categories?.map((category) => (
                            <div key={category.id} id={category.anchorId} className={css.faqVeiwsWrapQuestions_box}>
                                <h2>{category.name}</h2>
                                {category?.questions?.map((q) => (
                                    <Question
                                        onClick={activatedQuestion}
                                        item={q}
                                        key={q.id}
                                        isActive={q.id === activeQuestionId}
                                    />
                                ))}
                            </div>
                        ))}

                    {isMobile && question?.questions
                        ? question.questions?.map((item) => (
                              <Question
                                  onClick={activatedQuestion}
                                  item={item}
                                  key={item.id}
                                  isActive={item.id === activeQuestionId} // Pass active state to each question
                              />
                          ))
                        : isLoading && <Loader />}

                    {isLoading && <Loader />}

                    {isMobile && question?.questions !== null && question?.questions.length === 0 ? (
                        <div className={css.faqQuestion}>
                            <T textName='faqPage-faqQuestion' defaultStr='Пока нет вопросов ответов' page='/faq' />
                        </div>
                    ) : (
                        ''
                    )}

                    {isMobile && categories?.activeCategory && (
                        <button onClick={handleBack} className={css.faqButtonBack}>
                            {svgSprite('angle-left', {
                                fill: 'var(--darkColor)',
                                width: '6px',
                                height: '12px'
                            })}
                            <p className={css.faqButtonBackLabel}>
                                <T textName='faqPage-faqButtonBack' defaultStr='Вернуться назад' page='/faq' />
                            </p>
                        </button>
                    )}
                </div>

                {categories?.length && (
                    <div className={css.faqVeiwsWrapCategories}>
                        {categories?.map((item) => (
                            <Categories onClick={() => handleClickCategories(item.id)} categories={item} key={item.id} />
                        ))}
                        {loadMore ? (
                            <LazyLoad offset={500} onContentVisible={loadMore}>
                                <Loader compStyles={{ loader: css.loader }} />
                            </LazyLoad>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FAQ;
